import React from "react";
import {
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Box,
  Button,
  Text,
  HStack,
  IconButton,
  useColorModeValue,
} from "@chakra-ui/react";
import { RiMenuLine } from "react-icons/ri";
import { BsGrid } from "react-icons/bs";
import { FiFilter } from "react-icons/fi";
import SortBy from "../../../CommonComponents/Filter/SortBy";
import { common } from "../../../constants/constants";

const SearchTabs = ({
  list,
  index = 0,
  visible,
  setVisible,
  listGridIcon,
  onTabChange,
  sort,
  filterOptions,
  handleCheckboxChange,
  setApply,
  masterlistType,
  hasData,
  searchTerm,
  showFilterBox,
  onFilterToggle,
  filterAvailable,
}) => {
  const bgColor = useColorModeValue("white", "gray.800");
  const tabListBg = useColorModeValue("gray.50", "gray.700");
  const borderColor = useColorModeValue("gray.200", "gray.600");

  const handleTabChange = (newIndex) => {
    if (onTabChange) {
      onTabChange(newIndex);
    }
  };

  return (
    <Box
      border="1px solid"
      borderColor={borderColor}
      bg={bgColor}
      boxShadow="sm"
      overflow="hidden"
      width="100%"
    >
      <Tabs
        index={index}
        variant="unstyled"
        colorScheme="blue"
        onChange={handleTabChange}
      >
        <HStack
          justifyContent="space-between"
          bg={tabListBg}
          borderBottom="1px solid"
          borderColor={borderColor}
          px={2}
          py={2}
        >
          <TabList
            display="flex"
            flexDirection="row"
            gap={1}
            flex="1"
            overflowX="auto"
            css={{
              "&::-webkit-scrollbar": {
                height: "4px",
              },
              "&::-webkit-scrollbar-track": {
                background: "transparent",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "#cbd5e0",
                borderRadius: "2px",
              },
            }}
          >
            {list.map((item, index) => (
              <Tab
                key={item?.id || index}
                isDisabled={item?.isDisabled}
                fontWeight="600"
                fontSize="14px"
                width="max-content"
                color="gray.600"
                bg="transparent"
                borderRadius="lg"
                px={4}
                py={2.5}
                minH="44px"
                transition="all 0.2s ease"
                _hover={{
                  bg: "blue.50",
                  color: "blue.700",
                  transform: "translateY(-1px)",
                  boxShadow: "sm",
                }}
                _selected={{
                  color: "white",
                  bg: "linear-gradient(135deg, #4299e1 0%, #3182ce 100%)",
                  boxShadow: "md",
                  transform: "translateY(-1px)",
                  border: "1px solid",
                  borderColor: "blue.300",
                }}
                _active={{
                  transform: "translateY(0px)",
                }}
                _disabled={{
                  opacity: 0.5,
                  cursor: "not-allowed",
                  _hover: {
                    bg: "transparent",
                    transform: "none",
                    boxShadow: "none",
                  },
                }}
              >
                {item?.title}
                {item?.count !== undefined && item?.count > 0 && (
                  <Text
                    as="span"
                    ml={2}
                    px={2}
                    py={0.5}
                    borderRadius="full"
                    bg={item?.className === "count-label-green" ? "green.100" : "blue.100"}
                    color={item?.className === "count-label-green" ? "green.700" : "blue.700"}
                    fontSize="12px"
                    fontWeight="600"
                  >
                    {Number(item.count).toLocaleString()}
                  </Text>
                )}
              </Tab>
            ))}
          </TabList>
          <HStack gap={2} ml={2} alignItems="center">
            {filterAvailable && onFilterToggle && (
              <Button
                size="sm"
                fontSize="14px"
                fontWeight="600"
                onClick={onFilterToggle}
                leftIcon={<FiFilter size={16} />}
                variant={showFilterBox ? "solid" : "outline"}
                borderColor="blue.300"
                color={showFilterBox ? "white" : "blue.600"}
                bg={showFilterBox ? "blue.600" : "white"}
                _hover={{
                  bg: showFilterBox ? "blue.700" : "blue.50",
                  borderColor: "blue.400",
                  color: showFilterBox ? "white" : "blue.700",
                }}
                _active={{
                  bg: showFilterBox ? "blue.800" : "blue.100"
                }}
                transition="all 0.2s ease"
                borderRadius="lg"
                height="32px"
              >
                {common.FILTER}
              </Button>
            )}
            {sort && sort.length > 0 && hasData && (
              <Box>
                <SortBy
                  sort={sort}
                  masterlistType={masterlistType}
                  filterOptions={filterOptions}
                  handleCheckboxChange={handleCheckboxChange}
                  setApply={setApply}
                />
              </Box>
            )}
            {listGridIcon && (
              <HStack gap={0}>
                <IconButton
                  variant="unstyled"
                  onClick={() => setVisible(false)}
                  color={!visible ? "blue.500" : "gray.400"}
                  fontSize="20px"
                  display="flex"
                  borderTopLeftRadius="6px"
                  borderBottomLeftRadius="6px"
                  borderTopRightRadius="0px"
                  borderBottomRightRadius="0px"
                  border="1px"
                  borderColor={borderColor}
                  height="32px"
                  width="32px"
                  aria-label="List View"
                  _hover={{
                    bg: "gray.100",
                  }}
                >
                  <RiMenuLine />
                </IconButton>
                <IconButton
                  variant="unstyled"
                  onClick={() => setVisible(true)}
                  color={visible ? "blue.500" : "gray.400"}
                  fontSize="16px"
                  display="flex"
                  borderTopLeftRadius="0px"
                  borderBottomLeftRadius="0px"
                  borderTopRightRadius="6px"
                  borderBottomRightRadius="6px"
                  border="1px"
                  borderColor={borderColor}
                  height="32px"
                  width="32px"
                  aria-label="Grid View"
                  _hover={{
                    bg: "gray.100",
                  }}
                >
                  <BsGrid />
                </IconButton>
              </HStack>
            )}
          </HStack>
        </HStack>

        <TabPanels bg="white" width="100%" px={{base:"0",md:"15px"}}>
          {list.map((item, idx) => (
            <TabPanel key={`${item.id}-${idx}-${searchTerm || ''}`} px={0} py={0} width="100%">
              <Box as="section" width="100%" key={`${item.id}-panel-${idx}-${searchTerm || ''}`}>{item.component}</Box>
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>
    </Box>
  );
};

export default SearchTabs;

